import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { showToast } from "../primitives";
import {
  useAddWrappedApi,
  useFilterPreview,
  parseOperationIdsInput,
  isOAuthAuthKind,
  type WrappedApiAuthKind,
  type WrappedApiAuthScope,
} from "../../hooks/use-wrapped-apis";

/**
 * Sprint 16a — add a wrapped API. Paste an OpenAPI 3.0/3.1 URL, pick an auth
 * kind, name it. On submit the spec is fetched + parsed server-side and one
 * tool per GET operation is synthesised. Mirrors the NewTableModal pattern.
 */
export function AddWrappedApiModal({
  open,
  onOpenChange,
  onCreated,
  onUseWizard,
  seed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (slug: string) => void;
  /** Sprint 16d — switch to the AI setup wizard instead of this manual form. */
  onUseWizard?: () => void;
  /** Sprint 16d — pre-fill from the wizard's "Configure manually" escape (S16D.15). */
  seed?: { name?: string; slug?: string; url?: string };
}) {
  const addWrapper = useAddWrappedApi();

  const [name, setName] = useState(seed?.name ?? "");
  const [slug, setSlug] = useState(seed?.slug ?? "");
  const [slugEdited, setSlugEdited] = useState(!!seed?.slug);
  const [url, setUrl] = useState(seed?.url ?? "");
  const [authKind, setAuthKind] = useState<WrappedApiAuthKind>("bearer");
  const [authScope, setAuthScope] = useState<WrappedApiAuthScope>("per_user");
  const [baseUrl, setBaseUrl] = useState("");
  const [rateLimit, setRateLimit] = useState("");
  // OAuth config
  const [oauthClientId, setOauthClientId] = useState("");
  const [oauthClientSecret, setOauthClientSecret] = useState("");
  const [oauthAuthorizeUrl, setOauthAuthorizeUrl] = useState("");
  const [oauthTokenUrl, setOauthTokenUrl] = useState("");
  const [oauthScopes, setOauthScopes] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Sprint 16e — subset filter inputs (optional disclosure).
  const [filterOpen, setFilterOpen] = useState(false);
  const [pathPrefix, setPathPrefix] = useState("");
  const [operationIdsText, setOperationIdsText] = useState("");
  const [tag, setTag] = useState("");
  // Debounced copy of the filter inputs that drives the live preview (avoids
  // re-fetching the spec on every keystroke).
  const [debounced, setDebounced] = useState({ url: "", pathPrefix: "", operationIds: [] as string[], tag: "" });

  const isOAuth = isOAuthAuthKind(authKind);

  useEffect(() => {
    if (!filterOpen) return;
    const t = setTimeout(
      () => setDebounced({ url, pathPrefix, operationIds: parseOperationIdsInput(operationIdsText), tag }),
      500,
    );
    return () => clearTimeout(t);
  }, [filterOpen, url, pathPrefix, operationIdsText, tag]);

  const preview = useFilterPreview(
    debounced.url,
    { path_prefix: debounced.pathPrefix, operation_ids: debounced.operationIds, tag: debounced.tag },
    filterOpen,
  );

  useEffect(() => {
    if (open) {
      setName(seed?.name ?? "");
      setSlug(seed?.slug ?? "");
      setSlugEdited(!!seed?.slug);
      setUrl(seed?.url ?? "");
      setAuthKind("bearer");
      setAuthScope("per_user");
      setBaseUrl("");
      setRateLimit("");
      setOauthClientId("");
      setOauthClientSecret("");
      setOauthAuthorizeUrl("");
      setOauthTokenUrl("");
      setOauthScopes("");
      setError(null);
      setFilterOpen(false);
      setPathPrefix("");
      setOperationIdsText("");
      setTag("");
      setDebounced({ url: "", pathPrefix: "", operationIds: [], tag: "" });
    }
  }, [open]);

  if (!open) return null;

  // Auto-derive a slug from the name until the user edits the slug directly.
  function onNameChange(v: string) {
    setName(v);
    if (!slugEdited) {
      setSlug(
        v
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 32),
      );
    }
  }

  function close() {
    if (!addWrapper.isPending) onOpenChange(false);
  }

  async function handleSubmit() {
    setError(null);
    if (!name.trim()) return setError("Name is required.");
    if (!slug.trim()) return setError("Slug is required.");
    if (!/^[a-z0-9][a-z0-9-]{0,31}$/.test(slug)) {
      return setError("Slug must be lowercase letters, digits, and dashes (max 32).");
    }
    if (!url.trim()) return setError("OpenAPI spec URL is required.");

    const rl = rateLimit.trim() ? Number(rateLimit) : undefined;
    if (rl !== undefined && (!Number.isFinite(rl) || rl <= 0)) {
      return setError("Rate limit must be a positive number.");
    }

    if (isOAuth) {
      if (!oauthClientId.trim()) return setError("OAuth client ID is required.");
      if (!oauthClientSecret.trim()) return setError("OAuth client secret is required.");
      if (!oauthTokenUrl.trim()) return setError("OAuth token URL is required.");
      if (authKind === "oauth2_auth_code" && !oauthAuthorizeUrl.trim()) {
        return setError("OAuth authorize URL is required for the Authorization Code grant.");
      }
    }

    if (pathPrefix.trim() && !pathPrefix.trim().startsWith("/")) {
      return setError('Path prefix must start with "/" (e.g. /api/v2/tickets).');
    }
    const operationIds = parseOperationIdsInput(operationIdsText);

    try {
      const result = await addWrapper.mutateAsync({
        slug: slug.trim(),
        name: name.trim(),
        openapi_spec_url: url.trim(),
        auth_kind: authKind,
        auth_kind_scope: authScope,
        ...(baseUrl.trim() ? { base_url: baseUrl.trim() } : {}),
        ...(rl !== undefined ? { rate_limit_per_min: rl } : {}),
        ...(isOAuth
          ? {
              oauth_client_id: oauthClientId.trim(),
              oauth_client_secret: oauthClientSecret.trim(),
              oauth_token_url: oauthTokenUrl.trim(),
              ...(authKind === "oauth2_auth_code" ? { oauth_authorize_url: oauthAuthorizeUrl.trim() } : {}),
              ...(oauthScopes.trim() ? { oauth_scopes: oauthScopes.trim() } : {}),
            }
          : {}),
        // Sprint 16e — subset filter (only send dimensions the owner set).
        ...(pathPrefix.trim() ? { path_prefix: pathPrefix.trim() } : {}),
        ...(operationIds.length ? { operation_ids: operationIds } : {}),
        ...(tag.trim() ? { tag: tag.trim() } : {}),
      });
      const isFiltered = result.operation_count_after_filter < result.operation_count;
      showToast(
        isFiltered
          ? `Added ${result.wrapper.name} — wrapped ${result.operation_count_after_filter} of ${result.operation_count} operations (filtered). GET tools live; set your credential, then refresh tools.`
          : `Added ${result.wrapper.name} — ${result.read_only_operation_count} read tool${
              result.read_only_operation_count === 1 ? "" : "s"
            } live${result.write_operation_count ? `, ${result.write_operation_count} write op${result.write_operation_count === 1 ? "" : "s"} (enable to use)` : ""}`,
      );
      onOpenChange(false);
      onCreated(result.wrapper.slug);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add wrapped API.");
    }
  }

  return (
    <div
      style={overlay}
      onClick={(e) => e.target === e.currentTarget && close()}
      onKeyDown={(e) => e.key === "Escape" && close()}
    >
      <div style={modal} role="dialog" aria-modal="true" aria-label="Add wrapped API">
        <h2 style={{ fontSize: 16, fontWeight: 500, margin: "0 0 4px" }}>Add a wrapped API</h2>
        <p style={{ fontSize: 12, color: "var(--color-text-secondary)", margin: "0 0 16px", lineHeight: 1.5 }}>
          Point at an OpenAPI 3.0/3.1 spec to expose its endpoints as MCP tools. GET tools go live immediately;
          write operations are added disabled and enabled per-tool afterward.
        </p>

        {onUseWizard && (
          <button type="button" onClick={onUseWizard} style={wizardBanner}>
            <i className="ti ti-sparkles" style={{ fontSize: 15, color: "var(--color-text-info)" }} aria-hidden="true" />
            <span style={{ flex: 1, textAlign: "left" }}>
              <span style={{ fontWeight: 500, display: "block" }}>Set up with the AI wizard</span>
              <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                Let AI propose the slug, auth, operations, and mappings — accept or edit each step.
              </span>
            </span>
            <i className="ti ti-arrow-right" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
          </button>
        )}

        <Field label="Name">
          <input
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Zendesk"
            style={inputStyle}
            autoFocus
          />
        </Field>

        <Field label="Slug" hint="Used as the tool prefix and the /mcp/w/<slug> endpoint.">
          <input
            value={slug}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(e.target.value.toLowerCase());
            }}
            placeholder="zendesk"
            style={{ ...inputStyle, fontFamily: "var(--font-mono)" }}
          />
        </Field>

        <Field label="OpenAPI spec URL">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/openapi.json"
            style={inputStyle}
          />
        </Field>

        <Field label="Authentication">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(["bearer", "api_key", "basic", "oauth2_auth_code", "oauth2_client_credentials"] as WrappedApiAuthKind[]).map((k) => (
              <button key={k} type="button" onClick={() => setAuthKind(k)} style={authChip(authKind === k)}>
                {authKindLabel(k)}
              </button>
            ))}
          </div>
        </Field>

        {isOAuth && (
          <div style={oauthBox}>
            <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginBottom: 8 }}>
              OAuth client config — members authorise individually after setup{authKind === "oauth2_client_credentials" ? " (or, for client credentials, calls use these directly)" : ""}.
            </div>
            <Field label="Client ID">
              <input value={oauthClientId} onChange={(e) => setOauthClientId(e.target.value)} style={inputStyle} autoComplete="off" />
            </Field>
            <Field label="Client secret">
              <input type="password" value={oauthClientSecret} onChange={(e) => setOauthClientSecret(e.target.value)} style={inputStyle} autoComplete="off" />
            </Field>
            {authKind === "oauth2_auth_code" && (
              <Field label="Authorize URL">
                <input value={oauthAuthorizeUrl} onChange={(e) => setOauthAuthorizeUrl(e.target.value)} placeholder="https://provider/oauth/authorize" style={inputStyle} />
              </Field>
            )}
            <Field label="Token URL">
              <input value={oauthTokenUrl} onChange={(e) => setOauthTokenUrl(e.target.value)} placeholder="https://provider/oauth/token" style={inputStyle} />
            </Field>
            <Field label="Scopes (optional)">
              <input value={oauthScopes} onChange={(e) => setOauthScopes(e.target.value)} placeholder="read write" style={inputStyle} />
            </Field>
          </div>
        )}

        <Field label="Credential scope">
          <div style={{ display: "flex", gap: 6 }}>
            {(["per_user", "workspace"] as WrappedApiAuthScope[]).map((s) => (
              <button key={s} type="button" onClick={() => setAuthScope(s)} style={authChip(authScope === s)}>
                {s === "per_user" ? "Per-user" : "Service-account"}
              </button>
            ))}
          </div>
        </Field>

        <details
          open={filterOpen}
          onToggle={(e) => setFilterOpen((e.target as HTMLDetailsElement).open)}
          style={{ margin: "4px 0 12px" }}
        >
          <summary style={{ fontSize: 12, color: "var(--color-text-secondary)", cursor: "pointer" }}>
            Filter operations (optional)
          </summary>
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.5 }}>
              Large specs flood the tool list. Wrap only the operations you need — conditions combine with AND.
              Leave all blank to wrap everything.
            </div>
            <Field label="Path prefix" hint="Operations whose path starts with this (e.g. /api/v2/tickets).">
              <input
                value={pathPrefix}
                onChange={(e) => setPathPrefix(e.target.value)}
                placeholder="/api/v2/tickets"
                style={{ ...inputStyle, fontFamily: "var(--font-mono)" }}
              />
            </Field>
            <Field label="Operation IDs" hint="Comma- or newline-separated (e.g. listTickets, showTicket).">
              <input
                value={operationIdsText}
                onChange={(e) => setOperationIdsText(e.target.value)}
                placeholder="listTickets, showTicket"
                style={inputStyle}
              />
            </Field>
            <Field
              label="Tag"
              hint={
                preview.data?.available_tags.length
                  ? "Pick a tag detected in the spec, or type one."
                  : "Operations carrying this OpenAPI tag (e.g. Tickets)."
              }
            >
              <input
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="Tickets"
                list="wrapped-api-detected-tags"
                style={inputStyle}
              />
              <datalist id="wrapped-api-detected-tags">
                {(preview.data?.available_tags ?? []).map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </Field>
            <FilterPreviewLine
              loading={preview.isFetching}
              error={preview.isError}
              data={preview.data ?? null}
              hasUrl={!!url.trim()}
            />
          </div>
        </details>

        <details style={{ margin: "4px 0 12px" }}>
          <summary style={{ fontSize: 12, color: "var(--color-text-secondary)", cursor: "pointer" }}>
            Advanced (optional)
          </summary>
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
            <Field label="Base URL override" hint="Defaults to the spec's servers[0].url.">
              <input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://api.example.com"
                style={inputStyle}
              />
            </Field>
            <Field label="Rate limit (per user, per minute)" hint="Default 60.">
              <input
                value={rateLimit}
                onChange={(e) => setRateLimit(e.target.value)}
                placeholder="60"
                inputMode="numeric"
                style={inputStyle}
              />
            </Field>
          </div>
        </details>

        {error && <div style={errorStyle}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
          <button type="button" onClick={close} disabled={addWrapper.isPending} style={cancelBtn}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={addWrapper.isPending} style={primaryBtn}>
            {addWrapper.isPending ? "Fetching spec…" : "Add wrapped API"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12, flex: 1 }}>
      <span style={labelStyle}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{hint}</span>}
    </label>
  );
}

/** Sprint 16e — live "would wrap N of M operations" line for the filter disclosure. */
function FilterPreviewLine({
  loading,
  error,
  data,
  hasUrl,
}: {
  loading: boolean;
  error: boolean;
  data: { filtered_operations_count: number; total_operations: number } | null;
  hasUrl: boolean;
}) {
  let text: ReactNode;
  if (!hasUrl) text = "Enter a spec URL above to preview the filter.";
  else if (loading) text = "Checking spec…";
  else if (error) text = "Couldn't preview — check the spec URL is reachable.";
  else if (data)
    text = (
      <>
        This filter would wrap <strong>{data.filtered_operations_count}</strong> of {data.total_operations}{" "}
        operation{data.total_operations === 1 ? "" : "s"}.
      </>
    );
  else return null;
  return <div style={previewLine}>{text}</div>;
}

const previewLine: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "6px 10px",
};

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
  fontFamily: "var(--font-sans)",
};

const modal: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  padding: 24,
  width: 460,
  maxWidth: "92vw",
  maxHeight: "88vh",
  overflowY: "auto",
};

const labelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
};

const inputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "7px 10px",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
};

const errorStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--c-red-600, var(--color-text-primary))",
  background: "var(--c-red-50, var(--color-background-secondary))",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "6px 10px",
  marginBottom: 8,
};

const cancelBtn: CSSProperties = {
  fontSize: 13,
  padding: "7px 14px",
  borderRadius: "var(--border-radius-sm)",
  border: "var(--kuvio-border-default)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const primaryBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "7px 14px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const wizardBanner: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "10px 12px",
  marginBottom: 16,
  border: "1px solid var(--color-text-info)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--c-blue-50, var(--color-background-secondary))",
  color: "var(--color-text-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  fontSize: 13,
};

function authKindLabel(k: WrappedApiAuthKind): string {
  switch (k) {
    case "api_key": return "API key";
    case "oauth2_auth_code": return "OAuth (auth code)";
    case "oauth2_client_credentials": return "OAuth (client creds)";
    default: return k.charAt(0).toUpperCase() + k.slice(1);
  }
}

const oauthBox: CSSProperties = {
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "10px 12px",
  marginBottom: 12,
  background: "var(--color-background-secondary)",
};

function authChip(on: boolean): CSSProperties {
  return {
    fontSize: 12,
    padding: "5px 12px",
    borderRadius: "var(--border-radius-sm)",
    border: on ? "1px solid var(--color-text-info)" : "var(--kuvio-border-default)",
    background: on ? "var(--c-blue-50)" : "var(--color-background-primary)",
    color: on ? "var(--color-text-info)" : "var(--color-text-secondary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    fontWeight: on ? 500 : 400,
  };
}
